import * as fragments from './fragments';

const locale = Shopify.locale.toUpperCase();
const country = Shopify.country.toUpperCase();
const context = `@inContext(country: ${country}, language: ${locale})`;

export const bundleGet = (id) => `
query bundleGet ${context} {
  products(query: "tag:bundle_id::${id}", first: 9) {
    edges {
      node {
        ${fragments.product}
      }
    }
  }
}
`;

export const cartCreate = `
mutation cartCreate($input: CartInput!) {
  cartCreate(input: $input) {
    cart {
      id
    }
    userErrors {
      field
      message
    }
  }
}`;

export const cartBuyerIdentityUpdate = `
mutation cartCreate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
  cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
    cart {
      ${fragments.cart}
    }
    userErrors {
      field
      message
    }
  }
}`;

export const cartLinesAdd = `
mutation cartLinesAdd(
  $cartId: ID!,
  $lines: [CartLineInput!]!
)
${context} {
  cartLinesAdd(cartId: $cartId, lines: $lines) {
    cart {
      ${fragments.cart}
    }
    userErrors {
      field
      message
    }
  }
}`;

export const cartLinesRemove = `
mutation cartLinesRemove(
  $cartId: ID!,
  $lineIds: [ID!]!
)
${context} {
  cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
    cart {
      ${fragments.cart}
    }
    userErrors {
      field
      message
    }
  }
}`;

export const cartLinesUpdate = `
mutation cartLinesUpdate(
  $cartId: ID!,
  $lines: [CartLineUpdateInput!]!
)
${context} {
  cartLinesUpdate(cartId: $cartId, lines: $lines) {
    cart {
      ${fragments.cart}
    }
    userErrors {
      field
      message
    }
  }
}`;

export const cartAttributesUpdate = `
mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
  cartAttributesUpdate(attributes: $attributes, cartId: $cartId) {
    cart {
      ${fragments.cart}
    }
    userErrors {
      field
      message
    }
  }
}`;

export const collectionGet = `
query collectionGet($id: ID!, $first: Int!) ${context} {
  collection(id: $id) {
    products(first: $first) {
      edges {
        node {
          ${fragments.product}
        }
      }
    }
  }
}`;

export const productsByTagGet = `
  query productsByTagGet(
    $query: String!
  )
  ${context} {
    products(first: 100, query: $query) {
      edges {
        node {
          ${fragments.product}
        }
      }
    }
  }
`;

export const productsByTagGetExt = `
  query productsByTagGetExt(
    $query: String!
  )
  ${context} {
    products(first: 100, query: $query) {
      edges {
        node {
          ${fragments.product}
          descriptionHtml
          accordions: metafield(namespace: "details", key: "accordions") {
            references(first: 20) {
              edges {
                node {
                  ... on Metaobject {
                    fields {
                      key
                      value
                      reference {
                        ... on MediaImage {
                          image {
                            url
                            altText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          productIcons: metafield(namespace: "details", key: "product_icons") {
            references(first: 10) {
              edges {
                node {
                  ... on Metaobject {
                    fields {
                      key
                      value
                      reference {
                        ... on MediaImage {
                          image {
                            url
                            altText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          fullIngredients: metafield(namespace: "details", key: "full_ingredients") {
            value
          }
        }
      }
    }
  }
`;

export const productByHandleGet = `
query product (
  $handle: String!
)
${context} {
  productByHandle(handle: $handle) {
    ${fragments.product}
  }
}
`;

export const productByHandleGetExt = `
query product (
  $handle: String!
)
${context} {
  productByHandle(handle: $handle) {
    ${fragments.product}
    descriptionHtml
    accordions: metafield(namespace: "details", key: "accordions") {
      references(first: 20) {
        edges {
          node {
            ... on Metaobject {
              fields {
                key
                value
                reference {
                  ... on MediaImage {
                    image {
                      url
                      altText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    productIcons: metafield(namespace: "details", key: "product_icons") {
      references(first: 10) {
        edges {
          node {
            ... on Metaobject {
              fields {
                key
                value
                reference {
                  ... on MediaImage {
                    image {
                      url
                      altText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    fullIngredients: metafield(namespace: "details", key: "full_ingredients") {
      value
    }
  }
}
`;

export const productByIdGet = `
query product (
  $id: ID!
)
${context} {
  product(id: $id) {
    ${fragments.product}
  }
}
`;

export const metaObjectGet = (type, entryHandle) => `
  query getMetaobjectFieldValue {
    metaobject(
      handle: {handle: "${entryHandle}", type: "${type}"}
    ){
      fields {
        key
        value
        reference {
          ... on Product {
            ${fragments.product}
          }
        }
      }
    }
  }
`;

export const metaObjectByGIDGet = (metaObjectGID, key) => `
  query getMetaobjectFieldValue {
    metaobject(
      id: "gid://shopify/Metaobject/${metaObjectGID}"
    ){
      field(key: "${key}") {
        value
      }
    }
  }
`;

export const metafieldByHandleGet = (productHandle, namespace, key) => `
  query getMetafieldValue {
    productByHandle(handle: "${productHandle}") {
      metafield(key: "${key}", namespace: "${namespace}") {
        value
      }
    }
  }
`;

export const imageUrlByGIDGet = (imageGID) => `
  query getImageUrl {
    node(
      id: "gid://shopify/MediaImage/${imageGID}"
    ){
      ... on MediaImage {
        image {
          url
        }
      }
    }
  }
`;

export const blogGet = (tags) => `
query blogGet (
  $first: Int!,
  $reverse: Boolean!,
  $handle: String!,
  $sortKey: ArticleSortKeys!,
  $cursor: String
) ${context} {
  blogByHandle(handle: $handle){
    articles(
      first: $first,
      sortKey: $sortKey,
      reverse: $reverse,
      after: $cursor,
      query: "${tags.map((tag) => `tag:'${tag}'`).join(' OR ')}"
    ) {
      edges {
        node {
          ${fragments.article}
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

export const getBlogFacets = (tags) => `
query getBlogFacets {
    ${tags.map(
      (tag, index) => `
      q${index}: articles(first: 40, query: "tag:${tag}" ) {
        edges {
          node {
            id
            blog {
              id
            }
          }
        }
      },
    `,
    )}
  }
`;
